import type * as React from 'react'
import { useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Image,
  UpgradeDialog,
  Paragraph,
  Strong,
  List,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'

export const upgradePathName = 'ideasGroupLimit-upgrade'

type CtaOptions = {
  product?: string
  view?: string
  location: string
  button: string
  version?: number
}

function buildCtaString({
  product = 'create',
  view = 'board',
  location,
  button,
  version = 1,
}: CtaOptions): string {
  return `${product}-${view}-${location}-${button}-${version}`
}

type IdeaBoardGroupLimitPaywallProps = {
  cta: CtaOptions
  children: React.ReactNode
}

export function IdeaBoardGroupLimitPaywall({
  children,
  cta: ctaOptions,
}: IdeaBoardGroupLimitPaywallProps): JSX.Element {
  const currentOrganization = useCurrentOrganization()
  const openGlobalModal = useOpenGlobalModal()
  const [open, setOpen] = useState(false)
  const cta = buildCtaString(ctaOptions)

  const handleOpen = (isOpen: boolean): void => {
    if (isOpen) {
      BufferTracker.upgradePathViewed({
        clientName: Client.PublishWeb,
        product: 'publish',
        organizationId: currentOrganization?.id ?? '',
        upgradePathName,
        cta,
      })
    }
    setOpen(isOpen)
  }

  return (
    <UpgradeDialog open={open} onOpenChange={handleOpen}>
      <UpgradeDialog.Trigger>{children}</UpgradeDialog.Trigger>
      <UpgradeDialog.Content>
        <UpgradeDialog.TextContent>
          <UpgradeDialog.Title>
            You&apos;ve hit your limit of 3 groups on the free plan
          </UpgradeDialog.Title>
          <Paragraph>
            Upgrade your plan to unlock <Strong>unlimited groups</Strong>.
          </Paragraph>
          <List>
            <List.Item>
              <Paragraph>Up to 2,000 scheduled posts</Paragraph>
            </List.Item>
            <List.Item>
              <Paragraph>Analytics on best times to post</Paragraph>
            </List.Item>
            <List.Item>
              <Paragraph>Engagement tools to answer comments</Paragraph>
            </List.Item>
          </List>
          <UpgradeDialog.Actions>
            <Button
              variant="primary"
              size="large"
              onClick={(): void => {
                BufferTracker.cTAClicked({
                  clientName: Client.PublishWeb,
                  product: 'publish',
                  cta,
                  upgradePathName,
                  organizationId: currentOrganization?.id ?? '',
                })

                setOpen(false)
                openGlobalModal(MODALS.planSelector, {
                  cta: 'publish-createBoard-newGroupUpgradeModal-upgrade-1',
                  upgradePathName,
                  planSelector: {
                    initialStep: 'upgrade',
                  },
                })
              }}
            >
              See Upgrade Options
            </Button>
          </UpgradeDialog.Actions>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            src="https://buffer-publish.s3.amazonaws.com/images/ideas-group-paywall_x2.png"
            alt=""
            placeholder="https://buffer-publish.s3.amazonaws.com/images/ideas-group-paywall.png"
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
